<template>
  <div></div>
</template>

<script>
// PDF ponude i radnog naloga. Isti podaci kao račun, ali s naslovom "Ponuda" ili
// "Radni nalog", svim nazivima na
// hrvatskom i sa stavkama razvrstanim na robu i usluge prema documentDet.itemService.
//
// Namjerno je odvojena komponenta, a ne još jedna grana u DocumentPrintSales.vue -
// tamo se ispisuje račun i taj ispis se ne dira.
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { round } from '@/snippets/allSnippets'
import dayjs from 'dayjs'

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}
const money = (v) => {
  const n = round(num(v), 2)
  return n.toLocaleString('hr-HR', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}
const dat = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '')

// Isti raspored, samo drugi nazivi - ponuda i radni nalog se razlikuju u naslovu,
// napomeni u podnožju i tko ga je sastavio.
const VRSTE = {
  ponuda: {
    naslov: 'PONUDA',
    podnozje: 'Ponuda nije porezni dokument.',
    sastavio: 'Ponudu sastavio: ',
    rok: 'Vrijedi do: ',
    cijene: true
  },
  radninalog: {
    naslov: 'RADNI NALOG',
    podnozje: 'Radni nalog nije porezni dokument.',
    sastavio: 'Nalog izdao: ',
    rok: 'Rok izvršenja: ',
    // radni nalog ide bez cijena, PDV-a i iznosa
    cijene: false
  }
}

export default {
  name: 'documentponudanalogprint',
  data () {
    return {
      doc: {},
      items: [],
      comp: {},
      vrsta: VRSTE.ponuda,
      docDefinition: {}
    }
  },
  methods: {
    // koliko je komada na retku - ovisi o strani dokumenta
    kolicina (item) {
      const side = parseInt(this.doc.documentSide, 10)
      if (side === 1) { return num(item.itemPcsPlus) }
      if (side === 3) { return num(item.itemPcsNeutral) }
      return num(item.itemPcsMinus) || num(item.itemPcsPlus) || num(item.itemPcsNeutral)
    },

    // Radni nalog nema cijena - umjesto njih ostaje prazan stupac za bilješku.
    brojStupaca () {
      return this.vrsta.cijene ? 9 : 5
    },

    sirineStupaca () {
      return this.vrsta.cijene
        ? [18, '*', 24, 40, 45, 32, 50, 26, 55]
        : [18, '*', 30, 55, 150]
    },

    zaglavljeTablice () {
      if (!this.vrsta.cijene) {
        return [
          {text: 'R.br.', style: 'th', alignment: 'center'},
          {text: 'Naziv', style: 'th'},
          {text: 'Jmj', style: 'th', alignment: 'center'},
          {text: 'Količina', style: 'th', alignment: 'right'},
          {text: 'Napomena', style: 'th'}
        ]
      }
      return [
        {text: 'R.br.', style: 'th', alignment: 'center'},
        {text: 'Naziv', style: 'th'},
        {text: 'Jmj', style: 'th', alignment: 'center'},
        {text: 'Količina', style: 'th', alignment: 'right'},
        {text: 'Cijena', style: 'th', alignment: 'right'},
        {text: 'Rabat %', style: 'th', alignment: 'right'},
        {text: 'Osnovica', style: 'th', alignment: 'right'},
        {text: 'PDV %', style: 'th', alignment: 'center'},
        {text: 'Ukupno', style: 'th', alignment: 'right'}
      ]
    },

    redakStavke (item, redniBroj) {
      if (!this.vrsta.cijene) {
        return [
          {text: String(redniBroj), fontSize: 8, alignment: 'center'},
          {text: item.itemName + (item.itemDescription ? '\n' + item.itemDescription : ''), fontSize: 9},
          {text: item.itemUnit || '', fontSize: 8, alignment: 'center'},
          {text: money(this.kolicina(item)), fontSize: 9, alignment: 'right'},
          {text: ' ', fontSize: 9}
        ]
      }
      return [
        {text: String(redniBroj), fontSize: 8, alignment: 'center'},
        {text: item.itemName + (item.itemDescription ? '\n' + item.itemDescription : ''), fontSize: 8},
        {text: item.itemUnit || '', fontSize: 8, alignment: 'center'},
        {text: money(this.kolicina(item)), fontSize: 8, alignment: 'right'},
        {text: money(item.itemPrice), fontSize: 8, alignment: 'right'},
        {text: money(item.itemDiscountPercentage1), fontSize: 8, alignment: 'right'},
        {text: money(item.itemBaseAmountNoVAT), fontSize: 8, alignment: 'right'},
        {text: money(item.itemVAT1Percentage), fontSize: 8, alignment: 'center'},
        {text: money(item.itemAmountWithVAT), fontSize: 8, alignment: 'right'}
      ]
    },

    // Roba i usluge idu u zasebne skupine, svaka sa svojim zbrojem.
    grupeStavaka () {
      const roba = this.items.filter(i => i.itemService !== true)
      const usluge = this.items.filter(i => i.itemService === true)
      const grupe = []
      if (roba.length > 0) { grupe.push({naziv: 'Roba', stavke: roba}) }
      if (usluge.length > 0) { grupe.push({naziv: 'Usluge', stavke: usluge}) }
      // ako ni jedna stavka nema oznaku, ispiši ih bez podjele
      if (grupe.length === 0 && this.items.length > 0) {
        grupe.push({naziv: '', stavke: this.items})
      }
      return grupe
    },

    tijeloTablice () {
      const body = [this.zaglavljeTablice()]
      let redniBroj = 0
      this.grupeStavaka().forEach(grupa => {
        if (grupa.naziv) {
          const prazno = []
          for (let i = 1; i < this.brojStupaca(); i++) { prazno.push({}) }
          body.push([{text: grupa.naziv, colSpan: this.brojStupaca(), style: 'grupa'}].concat(prazno))
        }
        let osnovica = 0
        let ukupno = 0
        let kolicina = 0
        grupa.stavke.forEach(item => {
          redniBroj += 1
          body.push(this.redakStavke(item, redniBroj))
          osnovica += num(item.itemBaseAmountNoVAT)
          ukupno += num(item.itemAmountWithVAT)
          kolicina += this.kolicina(item)
        })
        if (grupa.naziv && !this.vrsta.cijene) {
          // bez cijena zbraja se samo količina
          body.push([
            {text: 'Ukupno ' + grupa.naziv.toLowerCase(), colSpan: 3, style: 'zbrojGrupe'},
            {}, {},
            {text: money(kolicina), style: 'zbrojGrupe', alignment: 'right'},
            {text: '', style: 'zbrojGrupe'}
          ])
        }
        if (grupa.naziv && this.vrsta.cijene) {
          body.push([
            {text: 'Ukupno ' + grupa.naziv.toLowerCase(), colSpan: 6, style: 'zbrojGrupe'},
            {}, {}, {}, {}, {},
            {text: money(osnovica), style: 'zbrojGrupe', alignment: 'right'},
            {text: '', style: 'zbrojGrupe'},
            {text: money(ukupno), style: 'zbrojGrupe', alignment: 'right'}
          ])
        }
      })
      return body
    },

    poreziPoStopama () {
      const ledger = {}
      this.items.forEach(item => {
        const stopa = round(num(item.itemVAT1Percentage), 2)
        if (!ledger[stopa]) { ledger[stopa] = {osnovica: 0, iznos: 0} }
        ledger[stopa].osnovica += num(item.itemBaseAmountNoVAT)
        ledger[stopa].iznos += num(item.itemVAT1Amount)
      })
      return Object.keys(ledger)
        .map(s => ({stopa: parseFloat(s), osnovica: ledger[s].osnovica, iznos: ledger[s].iznos}))
        .sort((a, b) => b.stopa - a.stopa)
    },

    // Bez argumenta uzima podatke iz storea (kao ostali ispisi). S argumentom
    // {doc, items, comp} ispisuje ono što je predano - tako POSfast može ispisati
    // ponudu iz košarice, prije nego što je dokument uopće spremljen.
    print (payload) {
      const src = payload || {}
      this.vrsta = VRSTE[src.vrsta] || VRSTE.ponuda
      this.doc = src.doc || this.$store.state.documentActivHead || {}
      this.items = src.items || this.$store.state.documentActivItems || []
      this.comp = src.comp || this.$store.state.companyActiv || {}

      if (this.items.length === 0) {
        this.$emit('nemaStavaka')
        return
      }

      const adresaTvrtke = [this.comp.CompanyStreet, this.comp.CompanyBuildingNameNumber].filter(x => x).join(' ')
      const mjestoTvrtke = [this.comp.CompanyPostcode, this.comp.CompanyCity].filter(x => x).join(' ')
      const adresaKupca = [this.doc.partnerStreet, this.doc.partnerBuildingNameNumber].filter(x => x).join(' ')
      const mjestoKupca = [this.doc.partnerPostcode, this.doc.partnerCity].filter(x => x).join(' ')

      const porezi = this.poreziPoStopama()
      const redoviPoreza = porezi.map(p => [
        {text: 'PDV ' + money(p.stopa) + ' % na ' + money(p.osnovica), fontSize: 9},
        {text: money(p.iznos) + ' EUR', fontSize: 9, alignment: 'right'}
      ])

      pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs

      this.docDefinition = {
        pageSize: 'A4',
        pageMargins: [30, 30, 30, 50],
        footer: (currentPage, pageCount) => ({
          columns: [
            {text: this.vrsta.podnozje, fontSize: 7, margin: [30, 0, 0, 0]},
            {text: 'Stranica ' + currentPage + ' od ' + pageCount, fontSize: 7, alignment: 'right', margin: [0, 0, 30, 0]}
          ]
        }),
        content: [
          {
            columns: [
              {
                width: '60%',
                stack: [
                  {text: this.comp.CompanyName || '', style: 'tvrtka'},
                  {text: adresaTvrtke, fontSize: 9},
                  {text: mjestoTvrtke, fontSize: 9},
                  {text: this.comp.CompanyVATID ? 'OIB: ' + this.comp.CompanyVATID : '', fontSize: 9},
                  {text: this.comp.CompanyPhones ? 'Tel: ' + this.comp.CompanyPhones : '', fontSize: 9},
                  {text: this.comp.Companyemail ? 'E-pošta: ' + this.comp.Companyemail : '', fontSize: 9}
                ]
              },
              {
                width: '40%',
                stack: [
                  {text: this.vrsta.naslov, style: 'naslov'},
                  {text: 'Broj: ' + (this.doc.documentId || ''), fontSize: 10, alignment: 'right'},
                  {text: 'Datum: ' + dat(this.doc.documentDate), fontSize: 10, alignment: 'right'},
                  {text: this.doc.documentInvoiceEndDate ? this.vrsta.rok + dat(this.doc.documentInvoiceEndDate) : '', fontSize: 10, alignment: 'right'}
                ]
              }
            ]
          },
          {text: ' ', margin: [0, 8, 0, 0]},
          {
            table: {
              widths: ['*'],
              body: [[{
                stack: [
                  {text: 'Kupac', style: 'oznaka'},
                  {text: this.doc.partnerName || '', fontSize: 10, bold: true},
                  {text: adresaKupca, fontSize: 9},
                  {text: mjestoKupca, fontSize: 9},
                  {text: this.doc.partnerVATID ? 'OIB: ' + this.doc.partnerVATID : '', fontSize: 9}
                ],
                margin: [4, 4, 4, 4]
              }]]
            },
            layout: 'lightHorizontalLines'
          },
          {text: ' ', margin: [0, 8, 0, 0]},
          {
            table: {
              headerRows: 1,
              widths: this.sirineStupaca(),
              body: this.tijeloTablice()
            },
            layout: 'lightHorizontalLines'
          },
          {text: ' ', margin: [0, 10, 0, 0]},
          // iznosi, PDV i podaci za plaćanje idu samo na ponudu
          this.vrsta.cijene
            ? {
              columns: [
                {width: '55%', text: ''},
                {
                  width: '45%',
                  table: {
                    widths: ['*', 'auto'],
                    body: [
                      [{text: 'Osnovica', fontSize: 9}, {text: money(this.doc.documentTotalAmountNetto) + ' EUR', fontSize: 9, alignment: 'right'}],
                      ...redoviPoreza,
                      [{text: 'ZA PLATITI', style: 'ukupno'}, {text: money(this.doc.documentTotalAmountBrutto) + ' EUR', style: 'ukupno', alignment: 'right'}]
                    ]
                  },
                  layout: 'lightHorizontalLines'
                }
              ]
            }
            : {text: ''},
          {text: ' ', margin: [0, 12, 0, 0]},
          this.vrsta.cijene
            ? {
              stack: [
                {text: this.doc.bankAccountNr ? 'Žiro račun (IBAN): ' + this.doc.bankAccountNr + (this.doc.bankAccountName ? ' - ' + this.doc.bankAccountName : '') : '', fontSize: 9},
                {text: 'Poziv na broj: ' + (this.doc.documentId || ''), fontSize: 9},
                {text: this.doc.documentDescription4 ? '\nNapomena: ' + this.doc.documentDescription4 : '', fontSize: 9}
              ]
            }
            : {
              stack: [
                {text: this.doc.documentDescription4 ? 'Napomena: ' + this.doc.documentDescription4 : '', fontSize: 9},
                {text: '\nIzvršio: ______________________', fontSize: 9, margin: [0, 14, 0, 0]},
                {text: 'Datum izvršenja: ______________', fontSize: 9, margin: [0, 6, 0, 0]}
              ]
            },
          {text: ' ', margin: [0, 20, 0, 0]},
          {
            columns: [
              {
                width: '50%',
                stack: [
                  {text: this.vrsta.sastavio + [this.$store.state.user.First, this.$store.state.user.Last].filter(x => x).join(' '), fontSize: 9},
                  {text: '_______________________________', fontSize: 9, margin: [0, 16, 0, 0]},
                  {text: 'potpis', fontSize: 7, color: '#666666'}
                ]
              },
              // na ponudi kupac potpisuje prihvat, na radnom nalogu stoji samo pečat
              this.vrsta.cijene
                ? {
                  width: '50%',
                  stack: [
                    {text: 'Prihvatio:', fontSize: 9},
                    {text: '_______________________________', fontSize: 9, margin: [0, 16, 0, 0]},
                    {
                      columns: [
                        {text: 'potpis kupca', fontSize: 7, color: '#666666'},
                        {text: 'M.P.', fontSize: 9, alignment: 'right'}
                      ]
                    },
                    {text: 'Datum: ____________________', fontSize: 9, margin: [0, 10, 0, 0]}
                  ],
                  alignment: 'left'
                }
                : {width: '50%', text: 'M.P.', fontSize: 9, alignment: 'right'}
            ]
          }
        ],
        styles: {
          naslov: {fontSize: 20, bold: true, alignment: 'right', margin: [0, 0, 0, 6]},
          tvrtka: {fontSize: 12, bold: true, margin: [0, 0, 0, 2]},
          oznaka: {fontSize: 8, color: '#666666', margin: [0, 0, 0, 2]},
          th: {fontSize: 8, bold: true, fillColor: '#eeeeee'},
          grupa: {fontSize: 9, bold: true, fillColor: '#f5f5f5', margin: [0, 2, 0, 2]},
          zbrojGrupe: {fontSize: 8, bold: true},
          ukupno: {fontSize: 12, bold: true}
        }
      }

      pdfMake.createPdf(this.docDefinition).open()
    }
  }
}
</script>
